import { ChatWidget } from "components/ChatWidget";
import { useContractManagementBot } from "hooks/useContractManagementBot";
import { sizer } from "layout/styles/styled/sizer";
import { TabPanel, TabView } from "primereact/tabview";
import { ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";

import { FeeScheduleComparison } from "../../FeeSchedule/components/FeeScheduleComparison";
import BaseRateFilesTable from "../../PayerRules/components/BaseRateFilesTable";
import { ContractsTable } from "../../PayerRules/components/ContractsTable";
import { BulkAppealsDashboard } from "./BulkAppealsDashboard";
import { ClaimsAnalysisDashboard } from "./ClaimsAnalysisDashboard";
import ClaimsList from "./ClaimsList";
import EstimateInsightsList from "./EstimateInsightsList";
import { ReprocessedClaimsDashboard } from "./ReprocessedClaimsDashboard";

const ContainerWrapper = styled.div`
  padding: ${sizer(2)};
`;

interface IContractsLibraryProps {
  accountId: string;
  accountName?: string;
  subtab?: string;
}

interface ISubtab {
  /** The URL segment: `/hospitals/view/:id/contracts/:name`. */
  name: string;
  header: string;
  element: ReactNode;
}

export function ContractsLibrary({
  accountId,
  accountName,
  subtab,
}: IContractsLibraryProps) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // CLE-2811 — same gate as Smart Insights and the contract page (useContractManagementBot).
  const hasContractManagementChatBot = useContractManagementBot(accountId);

  // Every sub-tab, in display order — the one list behind both the URL ↔ index lookup and the panels, so
  // a URL can never open (or a click navigate to) the neighbouring tab. The Contracts tab is shown to
  // SuperAdmin and HospitalStaff alike, and so is every sub-tab in it (CLE-2892): account scoping is the
  // API's job (checkAccountAccess), and the few SuperAdmin-only writes are gated on the controls themselves.
  const subtabs: ISubtab[] = [
    {
      name: "smart-insights",
      header: "Smart Insights",
      element: <FeeScheduleComparison accountId={accountId} />,
    },
    {
      name: "library",
      header: "Library",
      element: (
        <>
          {/*
            CLE-2811 — the contract chatbot, where the contracts are. It answers from the stored contract
            terms (signed date, timely filing, implants, escalators, reimbursement) via
            get_account_contracts; it cannot open contract documents.
          */}
          {hasContractManagementChatBot && (
            <ChatWidget
              accountId={accountId}
              chatType="ContractManagementBot"
              position="fixed"
              title="Contract Assistant"
              suggestedQuestions={[
                "Which payers have escalators in 2027?",
                "Which payers pay a percentage of Medicare?",
                "Which payers pay implants separately?",
                "What is the timely filing window for each payer?",
              ]}
              welcomeMessage="👋 Ask me about your contracts — when one was signed, the timely filing window, implant terms, which payers have escalators coming up, or who pays a percentage of Medicare. I answer from what's recorded on each contract, and I'll tell you when something isn't on file."
            />
          )}
          <ContractsTable accountId={accountId} showAddButton />
        </>
      ),
    },
    {
      name: "base-rate-files",
      header: "Base Rate Files",
      element: <BaseRateFilesTable accountId={accountId} />,
    },
    {
      name: "claims",
      header: "Claims",
      element: <ClaimsList accountId={accountId} />,
    },
    {
      name: "reprocessed-claims",
      header: "Reprocessed Claims",
      element: <ReprocessedClaimsDashboard accountId={accountId} />,
    },
    {
      name: "claim-analysis",
      header: "Claim Analysis",
      element: <ClaimsAnalysisDashboard accountId={accountId} />,
    },
    {
      name: "bulk-appeals",
      header: "Bulk Appeals",
      element: <BulkAppealsDashboard accountId={accountId} />,
    },
    {
      name: "estimate-insights",
      header: "Estimate Insights",
      element: <EstimateInsightsList accountId={accountId} />,
    },
  ];

  // The URL's sub-tab, or the first one (Smart Insights) when it is missing or unknown.
  const activeIndex = subtabs.findIndex((tab) => tab.name === subtab);
  const activeSubtabIndex = activeIndex === -1 ? 0 : activeIndex;

  const handleSubtabChange = (e: { index: number }) => {
    const newSubtab = subtabs[e.index];
    if (newSubtab) {
      navigate(`/hospitals/view/${id}/contracts/${newSubtab.name}`, {
        replace: true,
      });
    }
  };

  return (
    <ContainerWrapper>
      <TabView activeIndex={activeSubtabIndex} onTabChange={handleSubtabChange}>
        {subtabs.map((tab) => (
          <TabPanel key={tab.name} header={tab.header}>
            {tab.element}
          </TabPanel>
        ))}
      </TabView>
    </ContainerWrapper>
  );
}
